/**
 * DynFeaturePicker — the STEPPED FEATURE WIZARD, ported from the monolith's
 * `__dynModal` (Projects/Email/Smart Email Request/index.html L10278–10542).
 * 2026-08-20, per Jack: "for the dynamic portion i want to leverage the html
 * version."
 *
 * Choose communities → Floorplans → Homesites in ONE window, with the FEATURE
 * LEVEL control in the header deciding how long the chain is. Replaces the repo's
 * `LiveFillField` stopgap (Inspector.tsx L725), which was a search box, a chip row
 * and one "Fill from live content" button — no photo cards, no per-community
 * grouping, no caps, no steps.
 *
 * WHAT LIVES WHERE. Every decision (step chain, caps, button labels, breadcrumb,
 * card grouping, cap enforcement) is in `services/echDynPick.ts` and tested there
 * against the monolith's literals. This file is the shell: state, two fetches, and
 * markup. That split is why the wizard's rules are checkable at all — in the
 * monolith they only exist inside string concatenation.
 *
 * ONE FLOW, NOT TWO (monolith comment L12211). The panel's "Choose ›" and its
 * "+ Add a floorplan / + Add a home" both open THIS component at the community
 * step — there is no separate "add one more" picker. `DynSelectedPanel` calls the
 * same `onOpen`.
 *
 * TWO DELIBERATE DROPS from the monolith, both fetch-shaped:
 *  1. THE PER-NAME HERO PREFETCH CASCADE (`__lvRC` / `__lvResolveName`, L10386–10390):
 *     the monolith fires /sfmc-communities for 12 community names at a time purely
 *     to paint a thumbnail, re-rendering the grid after each batch. That is up to
 *     ~4 extra round trips before a single pick, for decoration. A community card
 *     here shows the hero it already has — from a loaded pack, or from the live
 *     search hit that produced it — and a gradient otherwise. Names from the
 *     division snapshot paint the gradient until the packs load, then gain photos.
 *  2. THE MASTERPLAN HERO BORROW (`__mpKey`, L10366): only meaningful with (1).
 * KEPT, because it is a real capability and not decoration: the "Search everywhere"
 * escape hatch (L10403 / `[data-mall]`) — /sfmc-communities on demand, so a
 * community outside the division snapshot is still reachable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
/* ── THE FEED CACHE, NOT `./api` DIRECTLY (2026-08-20) ─────────────────────────
   `echFeeds` is the page-level promise cache `useEchDyn` already pulls every feed
   through. The wizard joined it when the wizard stopped being the only thing that
   needs a pack it fetched: a community picked here now has to resolve at FILL time
   too, and StudioView re-fetches by code for a saved document that carries a pick.
   Sharing the cache is what keeps that "no second fetch layer" rather than two
   components asking /sfmc-content for the same code. */
import { feedCommunitySearch as searchCommunities, feedCommunityPack as getCommunityPack, feedRefdata, feedDirect } from "../services/echFeeds";
import { echDivisionStates, echStateAllowed } from "../services/echGeo";
import {
  wizSteps, capFor, breadcrumb, primaryLabel, searchPlaceholder, pickTitle,
  pickSubtitle, footerMax, addPick, groupedCards, groupedTable, tableHead, pickParentOf,
  type PickKind, type WizLevel, type DynCardVM,
} from "../services/echDynPick";
import { notSold, type Pack } from "../services/dynamicFill";
import { echNameVariants, echNameCore, echStateOfUrl } from "../services/echDyn";
import { noClosed } from "../services/snapshots";
import { resolveDirect, type DirectLevel } from "../services/echDirect";

/**
 * How many per-community counts one open will fetch.
 *
 * Lowered from 60 to 12 on 2026-08-22, after Jack: "why does it take so long to load."
 * A browser allows ~6 connections per host, so 60 queued `/sfmc-content` calls sit in
 * front of the `/plans.json` the step is actually waiting on — the counts were starving
 * the grid. Twelve covers what the menu shows without a scroll, two at a time, and the
 * rest fill in when a community is scoped (which fetches its pack anyway).
 */
const COUNT_CAP = 12;

/** what a finished wizard hands back — everything the fill needs, nothing global */
export interface DynPickResult {
  packs: Pack[];
  level: WizLevel;
  /** lower-cased community NAMES, the monolith's `sel` shape for the comms step */
  comms: string[];
  plans: string[];
  homes: string[];
}

interface Props {
  /** WIZARD MODE when set: the section that opened it (monolith `window.__dynWiz.sec`) */
  sec?: "fp" | "lst";
  /** the section's FEATURE LEVEL. The caller owns it — the wizard reports changes
   *  through `onLevel` rather than holding a second copy, because the fill that runs
   *  after Done reads the same value. (monolith `window.__dyn.secLevel[sec]`) */
  level: WizLevel;
  onLevel?: (l: WizLevel) => void;
  /** STANDALONE MODE: which single kind to pick. Ignored in wizard mode. */
  kind?: PickKind;
  /** Step 1's community names — this repo's `divComms(division)`, standing in for
   *  the monolith's read of the #community <select>'s options (`commList()`) */
  divisionComms?: string[];
  /**
   * The communities THIS REQUEST is about (Step 1's own picks), 2026-08-22.
   *
   * `divisionComms` arrives with them prepended, which is how they used to come first
   * — but once the step groups by area (Jack: "should group by area") first-in-the-array
   * stops meaning first-on-screen. Named separately, they get their own section at the
   * TOP, which is the same intent stated properly: the community you already told us
   * about should not be somewhere down an alphabetical list of fifty.
   */
  onRequest?: string[];
  /**
   * ── ADDING TO ONE SECTION (2026-08-22) ─────────────────────────────────────────
   * Jack: "wondering if add home should do Choose homes from inventory ›", then on
   * the mockup: "looks good". "+ Add a home" inside a section no longer drops a
   * blank card to type — it opens THIS picker already narrowed to that section's
   * community, capped at the slots the section has free.
   *
   * `scopeName` is the section's heading (a PARENT community name, the same value
   * `echParentCommunity` produces), matched against the loaded packs' parents rather
   * than a code, because a section heading is all the panel can promise: an empty
   * section has no home to read a code off.
   *
   * When it is set the level control and the scope dropdown are both hidden — the
   * level is homes by definition and the scope is the section — and `onHandType`
   * puts the old blank card back within reach as one footer link.
   */
  scopeName?: string;
  onHandType?: () => void;
  /**
   * The division whose community heroes to look up — 2026-08-20.
   *
   * Jack, on this grid: "none of loading". Every browse card was a blank navy gradient,
   * and it was a DATA gap, not a render bug: `divisionComms` is a list of NAMES out of
   * Airtable, which has no hero, so there was nothing to draw. A card only had a photo
   * if its pack was already loaded (Step 1's own pick) or it came back from the search
   * route, which returns `hero_image_url` — hence photos when searching, none when
   * browsing.
   *
   * `refdata` schema 7 carries `addr.communities[recId].hero`, joined in the worker from
   * SFMC by community code. Read here through `feedRefdata`, which is the page's
   * one-promise-per-route cache — the same call `useEchDyn` already makes, so this adds
   * NO request. The alternative was a lookup per card: ~50 to paint one grid, which is
   * why the port dropped the monolith's hero prefetch to begin with.
   */
  divisionCode?: string;
  /** picks already made, by kind (monolith `window.__dyn[kind]`) */
  picks?: { comms?: string[]; plans?: string[]; homes?: string[] };
  /** packs already loaded, if the caller has them (monolith `window.__lvPackCache`) */
  packs?: Pack[];
  /**
   * THE INVENTORY IS ALREADY KNOWN — DON'T GO LOOKING FOR IT. 2026-08-20.
   *
   * On an ECH block, Step 1 owns the scope and `useEchDyn` has already fetched every
   * pack the fill can resolve against: the picked community's, plus — when the pick
   * is a MASTERPLAN — one per child collection (`echPacks`). Handing them in does two
   * things the name → search → pack cascade below cannot:
   *   1. a MASTERPLAN resolves to its collections' plans. Its own /sfmc-content pack
   *      has none, so `loadPacks(["timber ridge"])` returns one empty pack and the
   *      Floorplans step draws nothing — the exact defect this closes;
   *   2. no second fetch layer. The same packs, fetched once, in one place.
   * Absent or empty, everything below behaves exactly as it did — that is what keeps
   * the legacy blocks (which are not tied to Step 1's scope) untouched.
   *
   * ── IT IS A SEED NOW, NOT AN OVERRIDE (2026-08-20, Jack: "i don't even need a
   * community — i click the + button and can select the info myself") ──────────────
   * This used to short-circuit `loadPacks` outright: hand in any `fixedPacks` and
   * EVERY community the requester picked resolved to them. That was invisible while
   * the community step was scoped to Step 1's one pick, and it becomes the whole bug
   * the moment the step offers the division again — pick Portland out of Apalachee
   * Hills and the wizard would hand back Arlington Heights' inventory. See `fixedFor`.
   */
  fixedPacks?: Pack[];
  /**
   * WHICH COMMUNITY NAMES `fixedPacks` STANDS FOR — 2026-08-20.
   *
   * Needed because a masterplan's packs are not named after it: `echPacks` puts the
   * masterplan's own pack first and its CHILD COLLECTIONS after, and when the sole
   * pack turns out to BE a collection it takes the collection's name. So "does any
   * fixed pack share this name" is not a sufficient test, and getting it wrong
   * reinstates the empty Floorplans step that `fixedPacks` was added to fix.
   * Step 1's own pick names go here; anything else the requester chooses is fetched.
   */
  fixedFor?: string[];
  onDone: (r: DynPickResult) => void;
  onClose: () => void;
  /** the monolith's `toast()` — cap refusals and step nags surface through it */
  onToast?: (msg: string) => void;
  /**
   * A HARD CAP ON THE PLANS/HOMES GRIDS, overriding `capFor` — 2026-08-20.
   *
   * `capFor` is the monolith's table and stays untouched (it is pinned to its literals
   * in echDynPick.test.ts); this is the CALLER saying how many slots its block
   * actually has. Layne's `ECH_Featured1CTA` renders one card and the fill reads the
   * FIRST pick, so its caller passes 1 — otherwise the wizard cheerfully takes ten
   * floorplans, the selected-cards panel shows ten tiles, and the canvas shows one.
   * The community step is never capped by this: its own cap is about how wide a net
   * the inventory is drawn from, not about slots.
   *
   * AT 1 THE GRID BECOMES SINGLE-SELECT rather than refusing — tapping a second card
   * REPLACES the first. A one-slot picker that answers "all 1 slots are used" is a
   * puzzle, not a control.
   */
  pickCap?: number;
}

const lc = (s: string) => String(s || "").toLowerCase();

/**
 * A COMMUNITY NAME REDUCED TO ITS IDENTIFYING WORDS — 2026-08-20.
 *
 * Used only as a FALLBACK, after the exact lookup misses. The static browse snapshot
 * (`divComms`) and Airtable's community records disagree on the decorative words:
 * "Destiny Collection - Star Valley" vs "Destiny - Star Valley", "Dream Series -
 * Sycamore Canyon" vs "Dream - Sycamore Canyon". Dropping "collection"/"series"/"the",
 * the roman-numeral phase suffixes and all punctuation makes those the same key.
 *
 * Deliberately NOT fuzzy — no edit distance, no substring matching. Two genuinely
 * different communities in one masterplan ("Discovery III" vs "Discovery IV") must not
 * collapse into each other, so the numeral is kept as a digit rather than removed.
 */
const ROMAN: Record<string, string> = { i: "1", ii: "2", iii: "3", iv: "4", v: "5", vi: "6" };
export const normName = (s: string): string =>
  lc(s)
    /* an apostrophe is DELETED, not spaced — otherwise "Planter's" becomes two tokens
       and never equals "Planters", which is exactly how the ATL list spells it */
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => ROMAN[w] || w)
    .filter((w) => w !== "collection" && w !== "collections" && w !== "series" && w !== "the" && w !== "at")
    .join(" ");

/* ── CARDS ⇄ LIST (Jack, 2026-08-21: "like the toggle so tackle the toggle") ──────
   Cards browse by photo; List scans thirty homes fast — one dense row each, the
   status shown IN THE PICKER ONLY. The choice persists per user and applies to the
   communities / floorplans / homesites steps alike; heroes stay cards (the photo IS
   the choice there). */
const VIEW_LS = "lsr_pickView";
const readView = (): "cards" | "list" => {
  try { return localStorage.getItem(VIEW_LS) === "list" ? "list" : "cards"; } catch { return "cards"; }
};

/** picker-only status chip — never any email's business */
function statusChip(s: string): { cls: string; label: string } | null {
  const u = String(s || "").toUpperCase().replace(/[\s-]+/g, "_");
  if (u === "MOVE_IN_READY") return { cls: "m", label: "Move-in ready" };
  if (u === "UNDER_CONSTRUCTION") return { cls: "u", label: "Under constr." };
  /* a floorplan not selling yet (2026-10-22) — listed now, so it has to say so */
  if (u === "COMING_SOON") return { cls: "cs", label: "Coming soon" };
  return null;
}

/* ── the dense LIST row — same selection semantics as the card ── */
function DynRow({ c, onPick }: { c: DynCardVM; onPick: () => void }) {
  const st = statusChip(c.status);
  return (
    <button type="button" className={"dynp-row" + (c.on ? " on" : "") + (c.disabled ? " dis" : "")} disabled={c.disabled} onClick={onPick}>
      <span className="dynp-rck">✓</span>
      <span className="dynp-rn">{c.name}</span>
      <span className="dynp-rm">{c.meta}</span>
      {!!c.price && (
        <span className="dynp-rp">
          {!!c.was && <s>${c.was.toLocaleString()}</s>}${c.price.toLocaleString()}
        </span>
      )}
      {c.badge && <span className="dynp-rtag drop">PRICE DROP</span>}
      {st && <span className={"dynp-rtag " + st.cls}>{st.label}</span>}
    </button>
  );
}

/**
 * The one thing a requester cannot tell by looking: how old these numbers are.
 *
 * Standup, 2026-08-28: status and pricing are pulled from Snowflake/Optimizely once a
 * day at 8 AM ET, and pricing can lag the website even then (Salesforce is the primary
 * source and is not always current either). Stated once, in a tooltip, where the prices
 * actually are.
 */
const DATA_REFRESH_TIP = "Homes, prices and statuses refresh once a day at 8 AM ET. A price can trail the website until the next refresh — check lennar.com if it has to be exact.";

/* ── the photo card (monolith __dynCard, L9802) ────────────────────────────────── */
function DynCard({ c, onPick }: { c: DynCardVM; onPick: () => void }) {
  return (
    <button
      type="button"
      className={"dynp-card" + (c.on ? " on" : "") + (c.disabled ? " dis" : "")}
      /* the monolith greys the card AND refuses the click; `addPick` refuses even if
         this attribute is somehow bypassed, so the cap holds either way */
      disabled={c.disabled}
      onClick={onPick}
    >
      {/* NO PLACEHOLDER CAPTION HERE, DELIBERATELY. The brief for this port asked for
          an "Images coming soon" placeholder, but that string belongs to the MANUAL
          repeater card (monolith L12842, `.repc-soont` + a 🏠 glyph). `__dynCard`
          (L9803) paints a bare navy gradient and says nothing — a picker card is
          80px of chrome and a caption there is noise. */}
      <span className={"dynp-cimg" + (c.img ? "" : " ph")} style={c.img ? { backgroundImage: `url('${c.img}')` } : undefined}>
        <span className="dynp-ck">✓</span>
        {c.badge && <span className="dynp-drop">PRICE DROP</span>}
        {!c.badge && statusChip(c.status)?.cls === "cs" && <span className="dynp-soon">COMING SOON</span>}
      </span>
      <span className="dynp-cn">{c.name}</span>
      <span className="dynp-cm">{c.meta}</span>
      {!!c.price && (
        <span className="dynp-cp">
          {!!c.was && <s>${c.was.toLocaleString()}</s>}${c.price.toLocaleString()}
        </span>
      )}
    </button>
  );
}

/** the keys two packs are the SAME community under: the code without leading zeros (the
    homes feed says "07367", the plans feed "7367"), and the lower-cased name */
export function packDupKeys(p: Pack): string[] {
  const code = String((p && p.code) || "").trim().replace(/^0+(?=\d)/, "");
  const name = String((p && p.name) || "").trim().toLowerCase().replace(/\s+/g, " ");
  const out: string[] = [];
  if (code) out.push("c:" + code);
  if (name) out.push("n:" + name);
  return out;
}

export default function DynFeaturePicker({
  sec, level, onLevel, kind: kindProp, divisionComms, onRequest, divisionCode, picks, packs: packsProp, fixedPacks, fixedFor, onDone, onClose, onToast, pickCap,
  scopeName, onHandType,
}: Props) {
  /* a section add is homes, scoped, and never shows the level or scope controls */
  const secAdd = String(scopeName || "").trim();
  const wiz = !!sec;
  const steps = useMemo(() => wizSteps(level), [level]);

  /* the CURRENT STEP. In wizard mode this walks the chain; standalone it is fixed.
     Note the monolith re-enters `__dynModal(k)` for each step, throwing the DOM away
     and re-reading `window.__dyn[kind]` — here the step is state and the selections
     live in one object, which is the same behaviour without the global. */
  /* ── JUMP STRAIGHT IN (2026-08-22) ────────────────────────────────────────────
     Jack: "would like to jump straight into each section … go direct to homesite
     level when clicking on it rather than select communities then select floor plans
     and etc." The wizard always opened on Communities because the item steps had no
     inventory until a community was picked; the division feeds removed that
     dependency (see echDirect.ts), so it opens ON the chosen level and Communities
     becomes the OPTIONAL narrowing step behind ‹ Back — which is where it belongs,
     since Step 1 has usually named the community already. */
  const [kind, setKind] = useState<PickKind>(wiz ? (level === "comms" ? "comms" : (level as PickKind)) : (kindProp || "comms"));
  /* the division-wide list is in play only while the requester has NOT narrowed to
     communities of their own; picking communities and pressing Next replaces it */
  /* ── EVERY LEVEL IS ITS OWN STEP (2026-08-22) ─────────────────────────────────
     `wizSteps` returns one step per level now (Jack: "each level should be completely
     separate"), so a wizard is ALWAYS on the step its level names. `direct` therefore
     just means "an item level", which is what it has meant in practice since the
     division feeds landed. */
  const [direct, setDirect] = useState<boolean>(wiz && level !== "comms");
  const [directErr, setDirectErr] = useState("");
  /* ── STEP 1 SCOPES THE DIRECT LIST (2026-08-22) ────────────────────────────────
     Jack: "should leverage first form to auto filter communities to find the homes
     quicker." A division list is the right SOURCE — 139 homes across 36 communities
     in JAX — but it is the wrong DEFAULT VIEW for a requester whose form already
     names Baldwin Ranch and Creekside. So the list opens filtered to Step 1's
     communities and says how many it is hiding, with one click to widen.
     The scope is `fixedPacks`' codes: Step 1's community, a masterplan's COLLECTIONS
     (which is why a masterplan request still finds its homes), and any community the
     requester has already pulled in this session. Names are carried too, because a
     collection can arrive with a name and no code. */
  /* ── SCOPE (T2, 2026-08-22) ────────────────────────────────────────────────────
     Jack on the old two-button row + "N more communities" note: "i want to rework
     how On this request / All of AZH / 6 more communities … is being displayed …
     cleaner and simpler, maybe even the search bar auto fills a community and you
     can search multiple". T2 won: ONE field, with the scope as a button on its right
     edge that opens a multi-select community menu.
       null  = the request's own communities (the default)
       []    = the whole division
       [..]  = an explicit pick from the menu */
  const [scopeSel, setScopeSel] = useState<string[] | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);

  /* Cards ⇄ List — persists per user, applies to every step but heroes */
  const [view, setView] = useState<"cards" | "list">(readView);
  const pickView = (v: "cards" | "list") => {
    setView(v);
    try { localStorage.setItem(VIEW_LS, v); } catch { /* storage can be absent */ }
  };

  /* ── THE SEED IS AN ARRAY OR IT IS NOTHING (2026-08-28) ────────────────────────
     Jack, at Floorplans level with two rows selected: the footer read "5002 selected ·
     max 10". Nothing here adds — the count is a `.length` — so a four-digit count means
     `sel.plans` was a STRING and `.length` counted its characters. `.slice()` is what
     let one through: on a string it is a no-op that returns a string, so a caller
     handing down the stored `__planPick` (now a newline-joined list, and before today a
     single `<code>|pn|<num>|<name>`) seeded a string that typechecks as `string[]` at
     the boundary and never at runtime. `addPick` then made it worse — `sel.concat([key])`
     on a string APPENDS THE KEY'S CHARACTERS, so every tap grew the "count" by about
     fifteen and `sel.length >= cap` compared characters against ten row slots.
     One coercion, at the only place the outside world gets in. */
  const asKeys = (v: unknown): string[] =>
    (Array.isArray(v) ? v : []).map((x) => String(x == null ? "" : x)).filter(Boolean);
  const [sel, setSel] = useState<{ comms: string[]; plans: string[]; homes: string[]; heroes: string[]; brand: string[] }>(() => ({
    comms: asKeys(picks?.comms).map(lc),
    plans: asKeys(picks?.plans),
    homes: asKeys(picks?.homes),
    heroes: [],
    brand: [],
  }));
  /**
   * THE SELECTION AS OF THE LAST TAP, not as of the last render — 2026-08-20.
   *
   * `sel` is the render's copy and lags inside a batch; taps that land together would
   * all read the same empty array and the last write would win. Jack saw exactly that:
   * "I selected 3 but only one is showing." Kept in step with `sel` on every write, and
   * resynced below whenever the caller hands down different `picks`.
   */
  const selRef = useRef(sel);
  const [packs, setPacks] = useState<Pack[]>(packsProp || []);
  const [q, setQ] = useState("");
  const [liveHits, setLiveHits] = useState<{ code: string; name: string; hero: string; city?: string; state?: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [connecting, setConnecting] = useState(false);
  /* ── DONE DOES NOT BLANK THE LIST (2026-08-22) ────────────────────────────────
     Jack: "when selecitng done you show this pop up for a sec for wahtever reason —
     this should not be there." `connecting` gates the whole modal body, so reusing
     it while a Done resolves its picks replaced the list the requester had just
     finished choosing from with "Loading the live inventory…" for a beat before the
     modal closed. Resolving a Done is a different state from having nothing to show:
     it keeps the list up and only occupies the button. */
  const [finishing, setFinishing] = useState(false);

  /* belt to the seed's braces: whatever reaches the count and the cap is a real array,
     so a future caller cannot reintroduce "5002 selected" from a different direction */
  const cur = asKeys(sel[kind as keyof typeof sel]);
  /* the caller's slot count beats the monolith's net width, on the item steps only —
     see `pickCap`. The comms step keeps `capFor`'s answer whatever the caller says. */
  const cap = pickCap && (kind === "plans" || kind === "homes") ? pickCap : capFor(kind, { wiz, level });

  /* ESC closes, like the repo's other modal (BynderPicker) and the monolith's
     overlay click-out. Both are wired; the monolith only has the click-out. */
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  /** monolith L10541: `if(kind!=="comms"&&!window.__lvPackCache)__lvPacks()` — a
   *  picker that needs inventory and has none loads it before it can draw anything */
  useEffect(() => {
    if (kind === "comms" || kind === "brand" || packs.length || connecting) return;
    const names = sel.comms;
    if (!names.length) return;
    setConnecting(true);
    loadPacks(names).then((p) => { setPacks(p); }).finally(() => setConnecting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const [commHero, setCommHero] = useState<Record<string, string>>({});
  /**
   * name → SFMC community CODE, from the same refdata read (2026-08-20).
   *
   * THE DEFECT THIS CLOSES, and it is the bigger half. Jack: "i selected 3 but only one
   * is showing… lot of homesites/floorplans missing." `loadPacks` resolved every picked
   * name by SEARCHING for it — `/sfmc-communities?q=<name>`, then `items.find(exact
   * name match)`. Airtable's label and the SFMC DE's `community_name` are not the same
   * string for plenty of communities ("Acacia Collection - Mandarina", "Destiny
   * Collection - Sycamore Canyon"), so the exact match missed, the `items[0]` fallback
   * took a NEIGHBOUR or the search returned nothing at all, and `if (!code) continue`
   * dropped that community WITHOUT A WORD. Three picked, one resolved, and the
   * Floorplans step then showed one community's inventory.
   *
   * refdata already carries the code — `addr.communities[recId].code`, the community
   * NUMBER — for every community in the division. Looking it up here is exact, needs no
   * request (the same cached `feedRefdata` the heroes come from), and removes a search
   * round trip per pick. The search path stays as the fallback for a name refdata does
   * not know, which is what the live-search hits are.
   */
  const [commCode, setCommCode] = useState<Record<string, string>>({});
  /**
   * name → its AREA and CITY, off the SAME refdata read (2026-08-22, Jack: "lets work
   * on showing some community info as well like a similair table" then "should group by
   * area").
   *
   * `addr.communities[recId].aoi` is the Area of Interest — the grouping the division
   * actually thinks in ("Jacksonville", "St. Augustine", "Green Cove Springs"). Measured
   * on JAX: 45 of 50 communities carry one, and `city` is present on essentially all of
   * them, so the group header is real data and the five without get their own bucket
   * rather than a guessed area. A community can list SEVERAL areas (comma-separated,
   * same shape `echCommsInAoi` parses); the first is the one it files under, so a
   * community appears once and the count in a header is a count of rows you can see.
   */
  const [commArea, setCommArea] = useState<Record<string, string>>({});
  const [commCity, setCommCity] = useState<Record<string, string>>({});
  /**
   * ── THE DIVISION'S COMMUNITIES COME FROM REFDATA, NOT THE BAKED SNAPSHOT (2026-08-30,
   * Jack: nine JAX cards blank, "it's critical we ensure that [it syncs]") ─────────────
   * `divisionComms` arrives from `divComms()` → `DIV_COMMS`, whose own header calls it
   * "Static per-division snapshot data — ported VERBATIM (generated)" from the monolith.
   * It is pasted into the source and never syncs, so its names drift from the live feed:
   * JAX still carried "Granary Park 40s - Granary Park" where the feed (and Airtable) now
   * say "40s - Granary Park". Because every decoration is looked up by lower-cased NAME,
   * a drifted name matches nothing and the card loses hero, aoi, city AND code together —
   * which then files it under "More in your division" for want of an area.
   *
   * refdata already returns the division's own community names (`ids.communities`), so it
   * is both the live roster and the thing the lookup is keyed on — using it makes the join
   * exact by construction rather than by luck.
   *
   * The snapshot stays as the FALLBACK, never as a merge: no `divisionCode`, a refdata
   * failure, or an empty payload all fall back to `divisionComms` so the step can never
   * come up empty. Merging the two would reintroduce the stale names as ghost cards.
   * `noClosed` is applied here too — the snapshot path filters sold-out communities and
   * the feed must not quietly readmit them.
   */
  const [commLive, setCommLive] = useState<string[]>([]);
  useEffect(() => {
    const div = String(divisionCode || "").trim();
    if (!div) return;
    let live = true;
    feedRefdata(div)
      .then((rd) => {
        if (!live) return;
        const ids = (rd && rd.ids && rd.ids.communities) || {};
        const bag = (rd && rd.addr && rd.addr.communities) || {};
        const hero: Record<string, string> = {};
        const code: Record<string, string> = {};
        const area: Record<string, string> = {};
        const city: Record<string, string> = {};
        Object.keys(ids).forEach((name) => {
          const row = bag[ids[name]];
          if (!row) return;
          if (row.hero) hero[lc(name)] = row.hero;
          if (row.code) code[lc(name)] = String(row.code);
          const a = String(row.aoi || "").split(",")[0].trim();
          if (a) area[lc(name)] = a;
          const ct = String(row.city || "").trim();
          const st = String(row.state || "").trim();
          if (ct) city[lc(name)] = st ? ct + ", " + st : ct;
        });
        /* the live roster — see `commLive` above. Set before the decorations so a render
           between the two never shows a card the feed has no row for. */
        const roster = noClosed(Object.keys(ids));
        if (roster.length) setCommLive(roster);
        if (Object.keys(hero).length) setCommHero(hero);
        if (Object.keys(code).length) setCommCode(code);
        if (Object.keys(area).length) setCommArea(area);
        if (Object.keys(city).length) setCommCity(city);
      })
      .catch(() => { /* decoration and a shortcut — never break the picker over it */ });
    return () => { live = false; };
  }, [divisionCode]);

  /**
   * monolith `wizConnect()` (L10508): community NAMES → codes → content packs.
   * Sequential, not Promise.all: /sfmc-content is the heaviest route the worker has
   * and the monolith's own parallel fan-out is what made the wizard feel like it
   * hung. Names that resolve to nothing are dropped here rather than carried as
   * `{code:null}` — this repo has no fill-time fan-out to rescue them.
   */
  const loadPacks = useCallback(async (names: string[]): Promise<Pack[]> => {
    const out: Pack[] = [];
    const seen: Record<string, 1> = {};
    const unresolved: string[] = [];
    /* the caller's pre-fetched inventory, and WHICH NAMES it answers for. `fixedFor`
       is Step 1's own pick names; the name match is the plain-community case, where
       the sole fixed pack is named after the community it came from. */
    const fixed = fixedPacks || [];
    const covered: Record<string, 1> = {};
    (fixedFor || []).forEach((n) => { if (n) covered[lc(n)] = 1; });
    fixed.forEach((p) => { if (p && p.name) covered[lc(p.name)] = 1; });

    for (const nm of names) {
      /**
       * ── ONE NAME AT A TIME, AND ONLY THE COVERED ONES SHORT-CIRCUIT (2026-08-20) ──
       * This used to be a single `if (fixedPacks.length) return fixedPacks;` above the
       * loop, which was correct exactly while the community step could only offer
       * Step 1's own pick. It offers the DIVISION again — that scoping was the right
       * fix for the wrong bug, see `DynLiveFill` — so an unconditional return would
       * now hand back Step 1's inventory for a community the requester deliberately
       * chose instead of it: the same silent cross-community no-op, moved one layer
       * out. A covered name still takes ALL the fixed packs (a masterplan's own pack
       * PLUS its collections, which is the empty-Floorplans-step fix); anything else
       * goes down the search → pack path below, once, through the shared feed cache.
       */
      if (covered[lc(nm)]) {
        for (const p of fixed) {
          /* same padded/unpadded collapse as below — a fixed pack and a fetched one
             for the same community must not both land (2026-08-29) */
          const k = p && p.code ? (/^\d+$/.test(p.code) ? String(Number(p.code)) : lc(p.code)) : "";
          if (!k || seen[k]) continue;
          seen[k] = 1;
          out.push(p);
        }
        continue;
      }
      const hit = liveHits.find((h) => lc(h.name) === lc(nm));
      /* refdata's own code first — exact, free, and the reason three picks now resolve
         to three packs instead of one. Only a name refdata doesn't know reaches the
         search below (a live-search hit from outside the division list). */
      let code = hit ? hit.code : (commCode[lc(nm)] || "");
      /**
       * ── THE STATIC BROWSE LIST DRIFTS FROM AIRTABLE, SO NORMALISE ONCE (2026-08-20) ──
       * Measured: `divComms("AZH")` offers 83 names and 5 of them are absent from
       * refdata's Airtable-sourced index — "Destiny Collection - Star Valley" is there
       * as "Destiny - Star Valley" and so on, the browse snapshot keeping a longer
       * marketing label than the record does. ATL: 0 of 50, so this is a tail, not the
       * rule. Collapsing the decorative words and the punctuation catches most of them
       * without a request; a genuine miss still falls through to the search.
       */
      if (!code) {
        const key = normName(nm);
        const alt = Object.keys(commCode).find((k) => normName(k) === key);
        if (alt) code = commCode[alt];
      }
      if (!code) {
        try {
          /* the pick's own spelling first, then SFMC's (the audience tag and the
             leading size marker are Airtable's, not theirs) — 2026-08-22 */
          let items: { code?: string; name: string; hero?: string }[] = [];
          for (const q of echNameVariants(nm)) {
            const rr = await searchCommunities(q).catch(() => null);
            items = (rr && rr.items) || [];
            if (items.length) break;
          }
          const exact = items.find((x) => lc(x.name) === lc(nm))
            || items.find((x) => normName(x.name) === normName(nm));
          /* ONLY TAKE items[0] WHEN IT IS THE ONLY ITEM. It used to be taken
             unconditionally, which is how a near-miss name quietly loaded a
             NEIGHBOURING community's inventory — worse than loading none, because
             nothing on screen says the wizard answered a different question. */
          code = String((exact || (items.length === 1 ? items[0] : null) || { code: "" }).code || "");
        } catch { code = ""; }
      }
      /* a name that resolves to nothing is REPORTED, not silently dropped — the whole
         complaint was three picks becoming one pack with no explanation */
      if (!code) { unresolved.push(nm); continue; }
      /* ── PADDED AND UNPADDED ARE THE SAME COMMUNITY (2026-08-29) ────────────────
         Measured live on dev, JAX → Granary Park → Homesites: 18 cards, 10 distinct.
         "50S - GRANARY PARK" and "GRANARY PARK 50S" rendered the SAME eight homes —
         one group under Airtable's label, one under SFMC's, because they arrived as
         two different code STRINGS for one community. refdata returns `7347`; the
         /sfmc-communities search returns `07347`. This dedupe compared them raw, so
         both survived, both fetched, and `getCommunityPack` — which accepts either
         spelling since BE #27 — cheerfully returned the same community twice.

         The padding split is documented on the backend side too (refdata.service.ts:
         "community_code is stored PADDED in Communities/Homesites and UNPADDED in
         …"), so comparing raw was always going to break somewhere; it broke here.

         Comparing on the digits is the whole fix. A non-numeric code (none seen in
         the wild, but the field is free text) falls back to its own lowercased form
         rather than collapsing to "". */
      const codeKey = /^\d+$/.test(code) ? String(Number(code)) : lc(code);
      if (seen[codeKey]) continue;
      seen[codeKey] = 1;
      try {
        const C = await getCommunityPack(code);
        if (C) out.push({ name: C.community?.name || nm, code, C });
      } catch { /* a community whose pack fails just brings no inventory */ }
    }
    if (unresolved.length && onToast) {
      onToast(
        unresolved.length === 1
          ? `No inventory found for ${unresolved[0]}`
          : `No inventory found for ${unresolved.length} communities: ${unresolved.join(", ")}`,
      );
    }
    return out;
  }, [liveHits, fixedPacks, fixedFor, commCode, onToast]);

  /* ── the community list (monolith commList(), L10357) ── the division snapshot
     first, then any live-search hits that aren't already in it */
  /* name -> hero for the whole division, from refdata schema 7. `feedRefdata` is the
     page's cached promise, so this is free; a schema <= 6 payload simply has no `hero`
     and every card keeps the gradient it had before. Names are lower-cased because the
     grid matches that way and Airtable's casing is not guaranteed to match the label. */

  /**
   * name → HOW MANY HOMES ARE AVAILABLE, per community
   * (2026-08-22, Jack: "show the actual number of homes" → "double check it… especially
   * if the communities are stating the number of available homes").
   *
   * ── THE DIVISION FEED CANNOT ANSWER THIS, MEASURED ─────────────────────────────
   * The first cut counted from `/homesites.json?div=` — one request for the whole
   * division. Driven live on JAX it returns **8 items for 50 communities**, and its own
   * meta says why: `basis:"salesforce-status"`, `sfMatched:8`, `staleShare:0.93`, while
   * `sfAvailCount:253`. It keeps only rows it can match to a Salesforce status, so it is
   * a sample, not a census. Checked against the per-community packs the same minute:
   * Avalon Woods 7 · Russell Retreat 3 · 50s - Granary Park 8 (of 27 raw). The feed
   * would have printed 1 for Avalon Woods and nothing for the other two — a wrong
   * number, which is worse than the filler it replaced.
   *
   * ── SO IT COMES FROM THE PACKS, LAZILY AND CACHED ──────────────────────────────
   * `/sfmc-content?community=<code>` is the number the fill itself uses
   * (`homesitesSource:"sf-enriched"`). One per community is the honest source; the cost
   * is managed rather than paid all at once: three in flight at a time, capped, and
   * every response goes through `feedCommunityPack`, so a community counted here is
   * already loaded for the Floorplans and Homesites steps and for the fill. A count
   * that has not arrived renders EMPTY — never a dash, never a zero.
   */
  const [commHomes, setCommHomes] = useState<Record<string, number>>({});

  const commRows = useMemo(() => {
    const out: { name: string; code?: string; hero?: string; live: boolean; area?: string; city?: string; homes?: number }[] = [];
    const seen: Record<string, 1> = {};
    const deco = (n: string) => ({ area: commArea[lc(n)], city: commCity[lc(n)], homes: commHomes[lc(n)] });
    /* ── THE LIVE NAME WINS, BUT THE ROSTER IS STILL A UNION — see `commLive` above ──
       First attempt REPLACED the snapshot with the feed and broke the division step:
       refdata's `ids.communities` is PARTIAL (only communities it holds a row for), so a
       straight swap dropped the division from 10+ cards to 1. The snapshot is stale, not
       wrong — it carries communities the feed has no row for, and a requester still has to
       be able to pick those.
       So: take every live name, then every snapshot name that is not the SAME community
       under a drifted spelling. The drift has one shape — the parent prepended to a name
       that already ends in it ("Granary Park 40s - Granary Park" for "40s - Granary Park")
       — so stripping that prefix is a precise test rather than a fuzzy one, and a genuine
       sibling ("50s - Granary Park") survives it untouched. */
    const deprefix = (n: string): string => {
      const s = String(n || "").trim();
      const i = s.lastIndexOf(" - ");
      if (i < 0) return s;
      const parent = s.slice(i + 3).trim();
      return parent && lc(s).startsWith(lc(parent) + " ") ? s.slice(parent.length).trim() : s;
    };
    const liveKeys: Record<string, 1> = {};
    commLive.forEach((n) => { liveKeys[lc(n)] = 1; liveKeys[lc(deprefix(n))] = 1; });
    const roster = commLive.length
      ? (onRequest || []).filter(Boolean)
        .concat(commLive)
        .concat((divisionComms || []).filter((n) => !liveKeys[lc(n)] && !liveKeys[lc(deprefix(n))]))
      : (divisionComms || []);
    roster.forEach((n) => { if (n && !seen[lc(n)]) { seen[lc(n)] = 1; out.push({ name: n, hero: commHero[lc(n)], live: false, ...deco(n) }); } });
    liveHits.forEach((h) => {
      if (seen[lc(h.name)]) return;
      seen[lc(h.name)] = 1;
      const d = deco(h.name);
      /* a hit the division's own data does not know gets its city/state from SFMC */
      const where = d.city || [h.city, h.state].filter(Boolean).join(", ");
      out.push({ name: h.name, code: h.code, hero: h.hero || commHero[lc(h.name)], live: true, ...d, city: where || d.city });
    });
    /* ── A MASTERPLAN'S ROW BORROWS ITS COLLECTIONS' FACTS (2026-08-22, Jack: "On this
       request 3 communities isn't showing any info") ─────────────────────────────────
       Step 1 names masterplans, and a masterplan has no city, no code and no inventory
       of its own — refdata carries `{city,state}` at best and often not even that — so
       those three rows were blank in every column. Its collections have all of it, and
       the parent is the sum of them: city from the first collection that has one, homes
       from the total. Only ever borrowed, never invented: a parent with no collections
       in the roster stays blank. */
    const coreOf = (n: string) => echNameCore(String(n || "")).trim().toLowerCase();
    out.forEach((p) => {
      if (p.city && p.homes != null) return;
      const pc = coreOf(p.name);
      if (!pc) return;
      const kids = out.filter((k) => k !== p && coreOf(k.name) !== pc && coreOf(k.name).indexOf(pc) > -1);
      if (!kids.length) return;
      if (!p.city) { const withCity = kids.find((k) => k.city); if (withCity) p.city = withCity.city; }
      if (p.homes == null) {
        const known = kids.filter((k) => typeof k.homes === "number");
        if (known.length) p.homes = known.reduce((n, k) => n + (k.homes || 0), 0);
      }
    });
    /* ── A COLLECTION'S CARD BORROWS ITS MASTERPLAN'S HERO (2026-08-30, Jack: nine
       community cards drawing the blank navy gradient) ────────────────────────────
       The mirror of the borrow above, on the same join. `SfmcCommunityHit.hero` is
       documented "may be empty for collections" (types/api.ts), so a collection has
       no photo of its own in the feed — and because `hero`, `aoi`, `city` and `code`
       are read off the SAME refdata row in one loop, a thin row loses all four at
       once: no photo, no city line, no count. The photo is the one of those a parent
       can honestly supply, because a collection IS the masterplan's land.

       Borrowed, never invented — a collection whose masterplan is not in the roster
       keeps its gradient. PLACEHOLDER art is deliberately not inherited: "Images
       coming soon" is a real CDN asset that the app already detects and sorts last
       (echOrder), so passing it down would spread it rather than fix anything.

       This does NOT move the card out of "More in your division" — that bucket is
       keyed on `aoi`, which no parent can supply. Populating Area of Interest on
       collection rows in Airtable is the upstream fix. */
    const PLACEHOLDER_RE = /comingsoon|coming-soon|placeholder|no-?image|\/default\//i;
    const realHero = (h?: string) => !!h && !PLACEHOLDER_RE.test(h);
    out.forEach((c) => {
      if (realHero(c.hero)) return;
      const cc = coreOf(c.name);
      if (!cc) return;
      /* the longest matching parent wins, the same rule `parentOf` uses below, so
         "Edenbrooke at Hyland Trail" beats a shorter accidental substring */
      let best: typeof c | undefined;
      out.forEach((p) => {
        if (p === c || !realHero(p.hero)) return;
        const pc = coreOf(p.name);
        if (!pc || pc === cc || cc.indexOf(pc) < 0) return;
        if (!best || pc.length > coreOf(best.name).length) best = p;
      });
      if (best) c.hero = best.hero;
    });
    /* SEARCH MATCHES THE AREA AND THE CITY TOO (2026-08-22) — typing "St. Augustine"
       should find its communities, not just a community with that in its name. */
    return out.filter((c) => !q || [c.name, c.area, c.city].some((s) => lc(s || "").indexOf(lc(q)) > -1));
  }, [divisionComms, commLive, onRequest, liveHits, q, commHero, commArea, commCity, commHomes]);

  useEffect(() => {
    if (kind !== "comms" && kind !== "brand") return;
    /* only communities whose CODE refdata gave us, and only ones not already counted */
    const todo = commRows
      .map((c) => ({ name: c.name, code: c.code || commCode[lc(c.name)] || "" }))
      .filter((c) => c.code && commHomes[lc(c.name)] == null)
      .slice(0, COUNT_CAP);
    if (!todo.length) return;
    let live = true;
    let i = 0;
    const worker = async (): Promise<void> => {
      while (live && i < todo.length) {
        const item = todo[i++];
        try {
          const C = await getCommunityPack(item.code);
          if (!live) return;
          const n = ((C && C.homesites) || []).filter(notSold).length;
          setCommHomes((m) => (m[lc(item.name)] === n ? m : { ...m, [lc(item.name)]: n }));
        } catch { /* one community's count is not worth a visible failure */ }
      }
    };
    /* THREE AT A TIME. /sfmc-content is the heaviest route the worker has and the
       monolith's own parallel fan-out is what made this wizard feel like it hung. */
    void Promise.all([worker(), worker()]);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, commRows, commCode]);

  /**
   * ── THE COMMUNITY STEP IS GROUPED BY AREA (2026-08-22, Jack: "should group by area") ──
   * One section per Area of Interest, alphabetical, communities alphabetical inside it.
   * Communities refdata has no area for land in "More in your division" LAST rather than
   * being scattered through the list or hidden — a requester still has to be able to
   * find them, and inventing an area for them would be worse than admitting there is
   * none. A live SFMC-search hit has no refdata row either, so it lands there too.
   */
  const commGroups = useMemo(() => {
    const NO_AREA = "More in your division";
    const ON_REQ = "On this request";
    const mine: Record<string, 1> = {};
    (onRequest || []).forEach((n) => { if (n) mine[lc(n)] = 1; });
    /* ── A MASTERPLAN TRAVELS WITH ITS COLLECTIONS (2026-08-22, Jack: "group the mpc
       with collections") ───────────────────────────────────────────────────────────
       Step 1 names the masterplan ("Edenbrooke at Hyland Trail | 55+"); the things a
       requester can actually pick are its collections ("50s - …", "60s - …"), and those
       were filed under their own Area of Interest, three groups away from the parent.
       Airtable's naming is the join: a collection carries the masterplan's core name
       (`echNameCore` strips the "| 55+" tag), which is the same rule `echCollectionsOf`
       falls back to. A collection of a masterplan on this request therefore sits in the
       masterplan's group, not in its area — one place to look, and the parent's own row
       can roll their numbers up (below). */
    const core = (n: string) => echNameCore(String(n || "")).trim().toLowerCase();
    const parents = (onRequest || []).map((n) => String(n || "")).filter(Boolean);
    const parentOf = (name: string): string => {
      const c = core(name);
      if (!c) return "";
      /* the longest matching parent wins, so "Edenbrooke at Hyland Trail" beats a
         shorter accidental substring */
      let best = "";
      parents.forEach((p) => {
        const pc = core(p);
        if (!pc || pc === c) return;
        if (c.indexOf(pc) > -1 && pc.length > core(best).length) best = p;
      });
      return best;
    };
    const by: Record<string, typeof commRows> = {};
    commRows.forEach((c) => {
      const k = mine[lc(c.name)]
        ? ON_REQ
        : (parentOf(c.name) ? ON_REQ : (String(c.area || "").trim() || NO_AREA));
      (by[k] = by[k] || []).push(c);
    });
    const rank = (a: string) => (a === ON_REQ ? -1 : a === NO_AREA ? 1 : 0);
    return Object.keys(by)
      .sort((a, b) => (rank(a) - rank(b)) || a.localeCompare(b))
      .map((area) => ({
        area,
        /* inside the request group, a parent is followed by its own collections */
        rows: by[area].slice().sort((x, y) => {
          if (area !== ON_REQ) return x.name.localeCompare(y.name);
          const px = parentOf(x.name) || x.name;
          const py = parentOf(y.name) || y.name;
          if (px !== py) return px.localeCompare(py);
          const isParent = (r: typeof x) => (mine[lc(r.name)] ? 0 : 1);
          return (isParent(x) - isParent(y)) || x.name.localeCompare(y.name);
        }),
      }));
  }, [commRows, onRequest]);


  const toggle = (key: string) => {
    /* A ONE-SLOT STEP IS SINGLE-SELECT, and it reuses `addPick`'s already-tested
       single-select branch (`brand`) rather than growing a second rule: tapping the
       picked card clears it, tapping another REPLACES. Passing `kind` here would
       refuse the second tap with "all 1 slots are used — remove one first", which on
       a one-card block reads as a bug. */
    /**
     * ── EVERY TAP READS THE PREVIOUS SELECTION, NOT THE RENDERED ONE (2026-08-20) ──
     *
     * Jack: "I selected 3 but only one is showing." This used to compute the next
     * selection from `cur` — the array captured when this render ran — and then hand
     * the finished array to `setSel`. Taps that land inside one React batch therefore
     * all start from the SAME empty array, each produces a one-item selection, and the
     * last write wins: three cards clicked, one card on, one community's inventory on
     * the next step. Deriving inside the updater makes each tap start from the state
     * the one before it produced, so the count is right however fast the taps arrive.
     *
     * The refusal toast has to be raised AFTER the update rather than inside it — a
     * state updater must stay pure, and React may invoke it twice in StrictMode.
     */
    /* A REF, NOT THE UPDATER ARGUMENT. Deriving inside `setSel` would also fix the
       batching, but the updater runs in the RENDER phase — after `toggle` has already
       returned — so a refusal discovered in there cannot be toasted from here, and the
       cap message silently stops appearing. The ref holds the same array the last tap
       produced, readable synchronously, so both the count and the refusal are right. */
    const prev = (selRef.current[kind as keyof typeof selRef.current] as string[]) || [];
    const r = addPick(prev, key, cap, cap === 1 ? "brand" : kind);
    if (r.refused) { if (r.message) onToast?.(r.message); return; }
    const nextSel = { ...selRef.current, [kind]: r.sel };
    selRef.current = nextSel;
    setSel(nextSel);
  };

  /* ── THE FEATURE-LEVEL CONTROL MOVES THE STEP WITH IT (2026-08-22) ────────────
     The control changes `level`, which re-derives `steps` — but the step being shown
     and the direct/narrowed mode are state, so without this the requester could flip
     to Homesites and still be looking at the Floorplans grid, or flip to Community
     and sit on a step that is no longer in the chain. Flipping to an item level
     re-enters direct mode: it is the same gesture as opening on that level. */
  const lastLevel = useRef(level);
  useEffect(() => {
    if (lastLevel.current === level) return;
    lastLevel.current = level;
    if (!wiz) return;
    loadTok.current += 1;
    setConnecting(false);
    setDirectErr("");
    if (level === "comms") { setKind("comms"); setDirect(false); setPacks([]); return; }
    setKind(level as PickKind);
    setDirect(true);
    setPacks([]);
  }, [level, wiz]);

  /* ── THE DIVISION FEED, LOADED FOR A DIRECT STEP (2026-08-22) ─────────────────
     One call per level per open: /homesites.json or /plans.json for this division,
     shaped into the same packs the grid already renders. It runs only in `direct`
     mode, so narrowing to communities (Back → pick → Next) still takes the original
     per-community path and nothing here competes with it.
     A FAILURE IS SAID OUT LOUD, not swallowed into an empty grid — an empty picker
     that looks like "no inventory" is the silent-empty fault this codebase keeps
     catching. */
  const loadTok = useRef(0);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!direct || (kind !== "plans" && kind !== "homes")) return;

    /* ── NO DIVISION IS NOT AN EMPTY DIVISION (2026-08-22) ──────────────────────
       Driven live in Jack's own tab: the picker read "No active floorplans came
       back for this division" while `/plans.json?div=ATL` was answering with 199
       plans. The request simply had no division on it, so the feed was never
       called — and the window blamed the data. A message that names the wrong
       cause is worse than no message: it sends the requester looking for missing
       inventory instead of at Step 1. */
    if (!String(divisionCode || "").trim()) {
      setConnecting(false); setPacks([]);
      setDirectErr("Pick a division on Step 1 first — this list is your division’s live inventory.");
      return;
    }
    let live = true;
    const tok = ++loadTok.current;
    setConnecting(true);
    setDirectErr("");
    /* warm the sibling level — homes ⇄ plans — so the next step is already loaded */
    const other: DirectLevel = kind === "homes" ? "plans" : "homes";
    void feedDirect(other, divisionCode || "").catch(() => { /* a warm never speaks */ });
    /* ── ONE FETCH PER LEVEL PER SESSION, AND WARM THE OTHER ONE (2026-08-22) ────
       Jack: "still takes forever to load floor plans." It was fetching on EVERY
       open — `fetchDirect` is the raw route, so nothing cached it — and
       `/plans.json?div=` is the heaviest thing the worker serves (measured
       2.4s–60s cold). `feedDirect` is the same call behind the shared promise
       cache every other feed uses, so the second open of a level is instant.
       And because the FEATURE LEVEL control now switches between separate steps,
       the level he has not opened yet is the one that will feel slow — so opening
       either level quietly warms the other. The warm is fire-and-forget: it must
       never surface an error or block the step the requester is looking at. */
    feedDirect(kind as DirectLevel, divisionCode || "")
      .then((ps) => {
        if (!live) return;
        /* ── STEP 1'S OWN PACKS WIN OVER THE DIVISION FEED (2026-08-22) ──────────
           The two sources filter differently and the division feed can be MISSING a
           community the request is about: driving this live, Granary Park 40s showed
           two homes in its own `/sfmc-content` pack and none in `/homesites.json`
           (that feed drops rows Salesforce calls sold or that look stale). Scoping to
           Step 1 then kept nothing, and the requester would have concluded their
           community had no inventory.
           So the already-fetched request packs are MERGED IN and win on their code —
           authoritative for the communities this email is about, with the division
           feed adding everything else for search. */
        const merged: Pack[] = [];
        const seen: Record<string, 1> = {};
        const mark = (p: Pack) => { packDupKeys(p).forEach((k) => { seen[k] = 1; }); };
        const dup = (p: Pack) => packDupKeys(p).some((k) => seen[k]);
        (fixedPacks || []).forEach((p) => {
          if (!p || !p.code) return;
          const has = kind === "homes" ? (p.C.homesites || []).length : (p.C.plans || []).length;
          if (!has) return;                       /* nothing of THIS kind to contribute */
          mark(p); merged.push(p);
        });
        ps.forEach((p) => { if (!dup(p)) { mark(p); merged.push(p); } });
        merged.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setPacks(merged);
        if (!merged.length) setDirectErr("No " + (kind === "homes" ? "available homes" : "floorplans") + " came back for this division.");
      })
      .catch((e) => {
        if (!live) return;
        /* ── A FAILED FEED DOES NOT DISCARD WHAT STEP 1 ALREADY LOADED (2026-08-22) ──
           `fixedPacks` are real packs already in hand — a masterplan's collections, or
           the picked communities' inventory. Showing them beats an empty grid with an
           error over it, and with one step per level this IS the requester's scope.
           The error is still said out loud when there is nothing at all to show. */
        const keep = (fixedPacks || []).filter((p) => p && p.code
          && (kind === "homes" ? (p.C.homesites || []).length : (p.C.plans || []).length));
        if (keep.length) { setPacks(keep as Pack[]); setDirectErr(""); return; }
        setDirectErr("Couldn’t load your division’s " + (kind === "homes" ? "homes" : "floorplans") + " — " + String((e && e.message) || e));
      })
      /* `live` guards the STATE WRITES of a superseded load; `tok` decides who owns
         the spinner. Without the token, narrowing away mid-flight left `connecting`
         true forever — the aborted load's `finally` skipped the reset (live===false)
         and no newer load existed to clear it, so the community grid never drew and
         the modal read "Loading the live inventory…" for good. Found by driving the
         narrow while the feed was still in flight (2026-08-22). */
      .finally(() => { if (loadTok.current === tok) setConnecting(false); });
    return () => { live = false; };
    /* divisionCode + the step are the inputs; `direct` gates it, and `fixedPacks`
       joins because Step 1's inventory is merged into the result above */
  }, [direct, kind, divisionCode, fixedPacks, reload]);

  /* ── step navigation (monolith wizNext / wizBack / wizFinish, L10517–10527) ──── */
  const finish = (loaded: Pack[]) => onDone({ packs: loaded, level, comms: sel.comms, plans: sel.plans, homes: sel.homes });

  const next = () => {
    const i = steps.indexOf(kind as WizLevel);
    const nxt = steps[i + 1];
    if (!nxt) return;
    if (kind === "comms") {
      /* monolith L10519: the chain cannot advance off an empty community step —
         everything downstream is inventory FROM those communities */
      if (!sel.comms.length) { onToast?.("Pick at least one community first"); return; }
      onToast?.("Loading your communities’ live content…");
      setConnecting(true);
      loadPacks(sel.comms)
        .then((p) => { setPacks(p); setKind(nxt); })
        .catch(() => setKind(nxt))
        .finally(() => setConnecting(false));
      return;
    }
    setKind(nxt);
  };
  const back = () => {
    const i = steps.indexOf(kind as WizLevel);
    const prv = steps[i - 1];
    if (!prv && !direct) return;
    /* stepping BACK out of a direct list is the requester asking to narrow: the
       division-wide packs are dropped so the comms step's own load owns them again,
       and the item selections go with them — a key from the division feed is not
       guaranteed to exist in the narrowed packs, and a pick that silently survives
       into a list that cannot resolve it is the vanishing-pick fault. */
    if (direct && (kind === "plans" || kind === "homes")) {
      setDirect(false);
      loadTok.current += 1;      /* whatever is in flight no longer owns the spinner */
      setConnecting(false);
      setDirectErr("");
      setPacks([]);
      const cleared = { ...selRef.current, plans: [], homes: [] };
      selRef.current = cleared;
      setSel(cleared);
      setKind("comms");   /* the narrowing step is the FIRST one, not the previous one */
      return;
    }
    setKind(prv);
  };
  const done = () => {
    /* ── A DIRECT PICK IS RESOLVED AGAINST THE REAL PACK BEFORE IT LEAVES (2026-08-22)
       The division feed and /sfmc-content read DIFFERENT data extensions, and a home's
       stored key carries its PRICE — so a key minted from the division list can fail
       to match the pack the fill later reads, and the pick would silently disappear.
       `resolveDirect` fetches the picked communities' real packs and remaps every key
       by LOT (plans by number/name), so the fill only ever sees packs it fetched
       itself. Anything that cannot be resolved is dropped and SAID, not stored. */
    if (direct && (kind === "plans" || kind === "homes")) {
      const chosen = (kind === "homes" ? selRef.current.homes : selRef.current.plans) || [];
      if (!chosen.length) { finish([]); return; }
      setFinishing(true);
      resolveDirect(kind as DirectLevel, packs, chosen)
        .then(({ packs: real, keys, dropped }) => {
          if (dropped.length && onToast) {
            onToast(dropped.length === 1
              ? "Couldn’t confirm " + dropped[0] + " in its community’s live content — left out."
              : dropped.length + " picks couldn’t be confirmed in their communities’ live content — left out.");
          }
          const next = { ...selRef.current, [kind]: keys } as typeof sel;
          selRef.current = next;
          setSel(next);
          onDone({ packs: real, level, comms: next.comms, plans: next.plans, homes: next.homes });
        })
        .catch(() => { onToast?.("Couldn’t load the live content for those picks — nothing was changed."); })
        .finally(() => setFinishing(false));
      return;
    }
    /* the last step of a Community-level chain has no packs yet — the picks ARE the
       communities, so they have to be resolved before the fill can run (monolith
       wizFinish L10528: `if(kind==="comms"){wizConnect().then(apply)}`) */
    if (kind === "comms" && !packs.length && sel.comms.length) {
      if (!sel.comms.length) { onToast?.("Pick at least one community first"); return; }
      onToast?.("Loading your communities’ live content…");
      setFinishing(true);
      loadPacks(sel.comms).then((p) => { setPacks(p); finish(p); }).finally(() => setFinishing(false));
      return;
    }
    finish(packs);
  };

  const searchEverywhere = () => {
    setSearching(true);
    /* ── THE NATIONAL SEARCH STAYS IN THE REQUEST'S OWN STATES (2026-09-09) ──────
       Katy, BAH, typed "Creekside": nine namesakes from three states came back with
       nothing to tell them apart, and four Texas homes went into a California email.
       A hit whose state the backend reports as outside the division's states is
       dropped; a hit with no state (older backend) is kept; every kept hit carries
       its city/state so the card can say where it is. */
    const okStates = echDivisionStates(divisionCode);
    /* ── THE STATE COMES FROM THE URL WHEN THE ROW HAS NONE (2026-09-10, measured on
       prod) ── BE #50 ships `state` from `whc_state`, but the search DE carries no
       such column: every one of the nine Creekside hits came back `state: ""`, so
       the filter above kept all nine and the label said nothing. The lennar.com url
       on the same row always names the state (/new-homes/california/…), and
       `echStateOfUrl` already reads it for the pack guard — read it here too. */
    const stateOf = (x: { state?: string; url?: string }) => x.state || echStateOfUrl(x.url);
    searchCommunities(q.trim())
      .then((r) => setLiveHits((r.items || [])
        .filter((x) => echStateAllowed(stateOf(x), okStates))
        .slice(0, 12)
        .map((x) => ({ code: x.code, name: x.name, hero: x.hero, city: x.city, state: stateOf(x) }))))
      .catch(() => { /* leave the snapshot list as-is */ })
      .finally(() => setSearching(false));
  };

  /* ── A DIRECT STEP IS A STEP OF ONE (2026-08-22) ──────────────────────────────
     Keeping the 3-step chain's breadcrumb while opening on its LAST step read as
     "Step 3 of 3" for a requester who had walked nothing — a progress indicator for
     progress that never happened. In direct mode there is no chain to show: no
     breadcrumb, the primary button is Done, and the secondary offers the narrowing
     path by name ("‹ Narrow by community") instead of a Back arrow to a step the
     requester was never on. */
  /* ── NO STEP COUNTER (2026-08-22, Jack: "the old format of selecting steps is still
     showing when selecting community") ─────────────────────────────────────────────
     "Step 1 of 2 · Communities › Floorplans" is the monolith's chrome. The FEATURE
     LEVEL control above already says what this wizard is for, and the primary button
     says where it goes next, so the counter was a third statement of the same thing —
     and the one Jack keeps seeing as leftover. `breadcrumb()` stays in echDynPick with
     its own tests: nothing else about the chain changed, only this line. */
  const crumb = null as ReturnType<typeof breadcrumb> | null;
  const isLast = !wiz || direct || steps.indexOf(kind as WizLevel) === steps.length - 1;
  /* the codes/names Step 1 put on this request */
  const scopeKeys = useMemo(() => {
    const codes: Record<string, 1> = {}; const names: Record<string, 1> = {};
    (fixedPacks || []).forEach((p) => { if (p && p.code) codes[String(p.code)] = 1; if (p && p.name) names[lc(p.name)] = 1; });
    (fixedFor || []).forEach((n) => { if (n) names[lc(n)] = 1; });
    return { codes, names, any: Object.keys(codes).length + Object.keys(names).length > 0 };
  }, [fixedPacks, fixedFor]);
  /* the community names the list is currently limited to — null means "no limit" */
  const activeScope = useMemo<string[] | null>(() => {
    if (scopeSel) return scopeSel.length ? scopeSel : null;   /* [] = all of the division */
    const req = Object.keys(scopeKeys.names);
    return req.length ? req : null;
  }, [scopeSel, scopeKeys]);
  const scoped = !!activeScope;
  const inScope = useCallback((p: Pack) => {
    if (!p) return false;
    if (!scopeSel) return !!((p.code && scopeKeys.codes[String(p.code)]) || (p.name && scopeKeys.names[lc(p.name)]));
    return !!(p.name && (activeScope || []).indexOf(lc(p.name)) > -1);
  }, [scopeSel, scopeKeys, activeScope]);
  /* every community the requester can choose between, with the count of THIS kind of
     item in each — the menu is useless without the counts (a community with 0 live
     homes is the one you need to see before you scope to it) */
  /**
   * ── THE MENU LISTS THE WHOLE DIVISION, NOT WHATEVER LOADED (2026-08-22) ─────────
   * Jack: "also not showing all communties in the drop down." It was built from `packs`
   * — the communities the feed happened to return — so on JAX it offered SIX, because
   * `/homesites.json?div=JAX` is answering with 8 rows across 6 communities
   * (`sfMatched:8` against its own `sfAvailCount:253`, `cacheState:"stale-revalidating"`
   * for 7h). A requester could not scope to a community the feed had missed, which is
   * most of them.
   * Now every community Step 1 knows about is listed. `n` is `null` where no pack has
   * been loaded yet — the count renders blank rather than 0, and picking that community
   * FETCHES its pack (see the scope-fill effect), which is the accurate source anyway.
   */
  const scopeComms = useMemo(() => {
    /* ── THE ROSTER IS THE LIST; PACKS ONLY SUPPLY COUNTS (2026-08-22) ─────────────
       Jack, twice: "multiple duplicates of the same community." Merging two SOURCES of
       rows could not stop it, because the same community arrives under two names AND,
       for at least one JAX collection (code 7318, "Silver Falls 50s at SilverLeaf"),
       under a code refdata does not carry — so neither the name nor the code matched
       and both rows survived.
       So there is one source of ROWS now: the division roster Step 1 uses. Counts are
       attached to it by code. A pack whose code is in no roster row is appended once at
       the end — that community is real and reachable, it just is not in the snapshot.
       Checked rows lead, so ticking one lifts it to the top ("when clicking on a
       community should move it to the top so can easily see which ones are checked"). */
    const codeKey = (c: string) => String(c || "").trim().replace(/^0+/, "");
    const countOf = (p: Pack) => (kind === "plans"
      ? (p.C.plans || []).length
      : (p.C.homesites || []).filter(notSold).length);
    const byCode: Record<string, number> = {};
    const byName: Record<string, number> = {};
    packs.forEach((p) => {
      const n = countOf(p);
      const ck = codeKey(String(p.code || ""));
      if (ck) byCode[ck] = n;
      if (p.name) byName[lc(p.name)] = n;
    });
    const seen: Record<string, 1> = {};
    const rows: { name: string; key: string; n: number | null; req: boolean; code: string }[] = [];
    (divisionComms || []).forEach((raw) => {
      const name = String(raw || "");
      const key = lc(name);
      if (!key || seen[key]) return;
      seen[key] = 1;
      const code = commCode[key] || "";
      const ck = codeKey(code);
      const n = ck && byCode[ck] != null ? byCode[ck]
        : byName[key] != null ? byName[key] : null;
      rows.push({
        name, key, n, code,
        req: !!((code && scopeKeys.codes[String(code)]) || scopeKeys.names[key]),
      });
    });
    /* a pack the roster has no row for — appended, never duplicated */
    packs.forEach((p) => {
      const nm = String(p.name || p.code || "");
      const key = lc(nm);
      const ck = codeKey(String(p.code || ""));
      if (!key || seen[key]) return;
      if (ck && rows.some((r) => codeKey(r.code) === ck)) return;
      seen[key] = 1;
      rows.push({
        name: nm, key, n: countOf(p), code: String(p.code || ""),
        req: !!((p.code && scopeKeys.codes[String(p.code)]) || scopeKeys.names[key]),
      });
    });
    const on = (r: { key: string }) => !activeScope || activeScope.indexOf(r.key) > -1;
    return rows.sort((a, b) => {
      if (on(a) !== on(b)) return on(a) ? -1 : 1;              /* ticked first */
      if ((a.n == null) !== (b.n == null)) return a.n == null ? 1 : -1;
      return ((b.n || 0) - (a.n || 0)) || a.name.localeCompare(b.name);
    });
  }, [packs, kind, scopeKeys, divisionComms, commCode, activeScope]);
  /* ── THE CHIP COUNTS THE ROWS IT TICKS (2026-09-25) ─────────────────────────────────
     Measured on prod: "2 communities" over a dropdown with ONE tick. `activeScope` is a set
     of NAMES, and Step 1's community reaches it twice — its own name and its pack's SFMC
     spelling ("50s - Granary Park" / "GRANARY PARK 50S"), or a masterplan next to its
     collection — while only the row whose name matches exactly is ticked. The filter was
     right (it also matches by code); only the label counted the wrong thing. It now reads
     the rows the menu actually ticks, so the chip and the menu can't disagree. */
  const tickedRows = activeScope ? scopeComms.filter((c) => activeScope.indexOf(c.key) > -1) : [];
  const scopeLabel = !activeScope
    ? `All of ${divisionCode || "my division"}`
    : (tickedRows.length || activeScope.length) === 1
      ? ((tickedRows[0] && tickedRows[0].name) || scopeComms.find((c) => c.key === activeScope[0])?.name || activeScope[0])
      : `${tickedRows.length || activeScope.length} communities`;
  const toggleScope = (key: string) => {
    const base = activeScope ? activeScope.slice() : scopeComms.map((c) => c.key);
    const i = base.indexOf(key);
    if (i > -1) base.splice(i, 1); else base.push(key);
    /* everything ticked is the same statement as "all of the division" */
    setScopeSel(base.length === scopeComms.length ? [] : base);
  };

  /* what the direct grid draws from: Step 1's communities first, everything else only
     when the requester widens. `hiddenByScope` is the count the widen button quotes —
     a filter that cannot be seen or counted is a list that looks broken. */
  const shownPacks = useMemo(() => {
    /* A SECTION ADD IS ONE COMMUNITY (2026-08-22): the parent name the section is
       headed with, matched the way the fill groups — so a masterplan's 50s and 60s
       both belong to the section headed with the masterplan. */
    /* ── ONE PACK PER COMMUNITY CODE (2026-08-22) ──────────────────────────────────
       Jack's floorplan list showed AVALON WOODS twice with the same two plans under
       each heading, and EMERALD ISLES TOWNHOMES the same way. Packs arrive from three
       places — Step 1's picks, the scope-fill effect and the direct division load —
       and any two of them can carry the same community, so the group list repeated it.
       Dedupe here, where every consumer (cards AND table) reads from. */
    /* ── …AND THE CODE IS COMPARED WITHOUT ITS LEADING ZEROS (2026-09-18 audit) ──────
       Still showing AVALON WOODS, EMERALD ISLES, HARDWICK FARMS and SILVER FALLS twice on
       a JAX market send, three weeks after the dedupe above. Measured on the feeds: the
       homes feed (which builds the market's Step-1 packs) spells the community code
       "07367"; the plans feed spells the same community "7367". Exact-string dedupe saw
       two communities. `packDupKeys` compares the code with leading zeros stripped, and
       the name as a second key, so the same community from two feeds is one heading. */
    const seen: Record<string, 1> = {};
    const uniq = packs.filter((p) => {
      if (!p || !String(p.code || "").trim()) return false;
      const ks = packDupKeys(p);
      if (ks.some((k) => seen[k])) return false;
      ks.forEach((k) => { seen[k] = 1; });
      return true;
    });
    if (secAdd) {
      const want = secAdd.toLowerCase();
      const mine = uniq.filter((p) => pickParentOf(p).toLowerCase() === want);
      if (mine.length) return mine;
      /* ── A RENAMED HEADING STILL FINDS ITS COMMUNITY, AND NEVER AN EMPTY GRID
         (2026-09-15) ─────────────────────────────────────────────────────────────
         Driven on dev: rename the section "Saddle Oaks" → "Saddle Oaks 50s" (Layne
         allows any 60-character heading), click "+ Add a home", and the picker said
         "Nothing matches." — the heading is the scope key, and the typed text matched
         no parent. Match either way round (heading contains the community, or the
         community contains the heading), and when nothing matches at all show the
         division rather than nothing — the same "never filter to nothing" rule the
         direct step has always had. */
      const loose = uniq.filter((p) => {
        const par = pickParentOf(p).toLowerCase();
        const nm = String(p.name || "").toLowerCase();
        return (par && want.indexOf(par) > -1) || nm.indexOf(want) > -1 || (nm && want.indexOf(nm) > -1);
      });
      return loose.length ? loose : uniq;
    }
    if (!direct || !scoped) return uniq;
    const kept = uniq.filter(inScope);
    /* never filter to nothing: a Step-1 community with no live inventory of this kind
       would leave an empty grid that reads as "the feed is broken" */
    return kept.length ? kept : uniq;
  }, [direct, scoped, packs, inScope, secAdd]);
  const groups = kind === "plans" || kind === "homes" || kind === "heroes"
    ? groupedCards(shownPacks, kind, q, cur, { cap })
    : [];
  /* THE LIST VIEW IS A TABLE for floorplans and homesites — Jack, 2026-08-22, after
     approving T2: "don't need it for communities though just floor plans and
     homesites". The community step keeps its cards: the photo is the choice there,
     and a community row has no homesite number to give a column to. */
  const tableMode = view === "list" && (kind === "plans" || kind === "homes");
  /* the scope control only earns its space on a DIVISION-WIDE item step with more
     than one community to choose between */
  /* THE CONTROL APPEARS WHEN THE LOADED INVENTORY SPANS MORE THAN ONE COMMUNITY —
     counted on PACKS, not on the menu (2026-08-22). The menu now lists the whole
     division, so keying this off its length would have shown a scope control on a
     single-community step, which is nothing to scope. */
  const scopeCtl = !secAdd && direct && (kind === "plans" || kind === "homes") && packs.length > 1;

  /**
   * ── SCOPING TO A COMMUNITY FETCHES ITS INVENTORY (2026-08-22) ───────────────────
   * The menu can now name a community the division feed never returned, so ticking it
   * has to be able to produce homes. It resolves the way the count on the community
   * step does — the community's own `/sfmc-content` pack, which is the accurate source
   * (`homesitesSource:"sf-enriched"`) and the one the fill uses. Cached, so a community
   * scoped here is already loaded for the next step and for the fill.
   * Only for communities with a code and no pack yet; three at a time; and it never
   * touches `packs` for a community that already has one, so nothing that loads
   * correctly today changes.
   */
  useEffect(() => {
    if (!scopeCtl || !activeScope || !activeScope.length) return;
    const have: Record<string, 1> = {};
    packs.forEach((p) => { have[lc(p.name || p.code)] = 1; });
    const want = activeScope
      .filter((k) => !have[k])
      .map((k) => ({ key: k, code: commCode[k] || "" }))
      .filter((x) => x.code)
      .slice(0, 10);
    if (!want.length) return;
    let live = true;
    let i = 0;
    const nameOf: Record<string, string> = {};
    (divisionComms || []).forEach((n) => { nameOf[lc(n)] = String(n); });
    const worker = async (): Promise<void> => {
      while (live && i < want.length) {
        const it = want[i++];
        try {
          const C = await getCommunityPack(it.code);
          if (!live || !C) continue;
          setPacks((ps) => (ps.some((p) => lc(p.name || p.code) === it.key)
            ? ps
            : ps.concat([{ code: it.code, name: nameOf[it.key] || it.key, C }])));
        } catch { /* one community failing to load is not the picker failing */ }
      }
    };
    void Promise.all([worker(), worker(), worker()]);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeCtl, activeScope, commCode, divisionComms]);
  const tgroups = tableMode ? groupedTable(shownPacks, kind, q, cur, { cap }) : [];

  return (
    <div className="dynp-ovl" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dynp-modal">
        <div className="dynp-hd">
          <div>
            {/* the dot sits BESIDE the title, never inside it: `.dynp-ttl`'s text is the
                panel's identity and three suites assert it exactly, so an "i" swept into
                that node is a defect the moment it is added (measured: five failures) */}
            <div className="dynp-ttlrow">
              <div className="dynp-ttl">{pickTitle(kind)}</div>
              {/* ── WHEN THE PRICES AND STATUSES WERE LAST TRUE (2026-08-28 standup) ──
                  Homes status and pricing come from Snowflake/Optimizely and refresh
                  ONCE A DAY at 8 AM ET, so a price here can trail the website by most of
                  a day — the call asked for that to be visible. A tooltip and not a line
                  of copy: the panel's job is to show the homes, and a standing paragraph
                  about data cadence is exactly the clutter this app keeps removing. */}
              <span className="dynp-i" data-tip={DATA_REFRESH_TIP} aria-label={DATA_REFRESH_TIP}>i</span>
            </div>
            <div className="dynp-sub">{pickSubtitle(kind, { wiz, level, cap, n: cur.length, direct })}</div>
          </div>
          <button type="button" className="dynp-x" onClick={onClose} title="Close">✕</button>
        </div>

        {/* FEATURE LEVEL — the community step only, and only in wizard mode
            (monolith L10450). Changing it re-derives `steps`, so the primary button
            can flip between Next and Done without the step changing. */}
        {/* ── THE LEVEL CONTROL BELONGS ON A DIRECT STEP TOO (2026-08-22) ──────────
            It used to render only on the community step, which was harmless while
            every wizard STARTED there. Opening straight onto Homesites hid it, and
            hiding it defeats the ask it was built for — "jump straight into each
            section … should adapt to each level" means switching sections has to be
            one click, not a narrow-then-switch-then-back-out. Shown on the comms step
            and on any direct step; hidden only mid-chain, where changing the level
            would rewrite a chain the requester is halfway through. */}
        {!secAdd && wiz && (kind === "comms" || direct) && (
          <div className="dynp-lvl">
            <div className="dynp-lvlh">Feature level</div>
            <div className="dynp-lvlseg">
              {([["comms", "Community"], ["plans", "Floorplans"], ["homes", "Homesites"]] as [WizLevel, string][]).map(([v, lab]) => (
                <button key={v} type="button" className={level === v ? "on" : ""}
                  onClick={() => {
                    onLevel?.(v);
                  }}>{lab}</button>
              ))}
            </div>
          </div>
        )}

        {/* the breadcrumb, absent on a single-step chain (monolith `steps.length>1`) */}
        {crumb && (
          <div className="dynp-crumb">
            Step {crumb.index} of {crumb.total} ·{" "}
            {crumb.parts.map((p, i) => (
              <span key={p.label}>
                {i > 0 && <span className="dynp-sep"> › </span>}
                {p.current ? <b>{p.label}</b> : p.label}
              </span>
            ))}
          </div>
        )}

        <div className="dynp-qw">
          <div className={"dynp-one" + (scopeCtl ? " sc" : "")}>
            <input className="dynp-q" placeholder={searchPlaceholder(kind)} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            {scopeCtl && (
              <>
                <button type="button" className="dynp-sc" aria-expanded={scopeOpen}
                  onClick={() => setScopeOpen((v) => !v)}>
                  {scopeLabel}<span aria-hidden="true"> ▾</span>
                </button>
                {scopeOpen && (
                  <div className="dynp-scmenu">
                    <div className="dynp-scmh">
                      <span>Communities in {divisionCode || "your division"}</span>
                      <button type="button" onClick={() => setScopeSel(activeScope ? [] : null)}>
                        {activeScope ? "Select all" : "This request only"}
                      </button>
                    </div>
                    {/* ── THE WAY BACK TO THE CHAIN LIVES HERE NOW (2026-08-22) ────
                        Jack, twice: "why do you still have this button ‹ Narrow by
                        community… should be removed." It was a standalone footer button
                        next to a dropdown that does the narrowing, which reads as two
                        controls for one job. It is not the same job — this leaves direct
                        mode and walks community → floorplans → homesites — but it belongs
                        with the community controls, not in the footer. One control, in
                        one place: the footer keeps it only when there is no dropdown to
                        put it in (a single-community step). */}
                    {scopeComms.map((c) => (
                      <label key={c.key} className={c.req ? "req" : undefined}>
                        <b>{c.name}</b>
                        {/* blank, not 0, until this community's inventory is known */}
                        <i>{c.n == null ? "" : c.n}</i>
                        <input type="checkbox" aria-label={c.name}
                          checked={!activeScope || activeScope.indexOf(c.key) > -1}
                          onChange={() => toggleScope(c.key)} />
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {/* heroes stay cards — the photo is the choice there */}
          {kind !== "heroes" && (
            <span className="dynp-view" role="group" aria-label="View">
              <button type="button" className={view === "cards" ? "on" : ""} onClick={() => pickView("cards")}>Cards</button>
              <button type="button" className={view === "list" ? "on" : ""} onClick={() => pickView("list")}>List</button>
            </span>
          )}
        </div>

        <div className="dynp-bd">
          {connecting && <div className="dynp-msg">Loading the live inventory…</div>}

          {/* the COMMUNITY step: flat card grid off the division list + live hits */}
          {!connecting && (kind === "comms" || kind === "brand") && (
            <>
              {!commRows.length && !searching && (
                <div className="dynp-msg">No matches{q.length >= 2 ? " in your division — try “Search everywhere” below." : "."}</div>
              )}
              {/* ── ONE SECTION PER AREA (2026-08-22) ─────────────────────────────
                  The header carries the area and its count, the same shape the
                  Floorplans / Homesites steps already group by community with. List
                  view gains two real columns — city/state and what the feed knows —
                  so the step reads as a table instead of fifty bare names. */}
              {commGroups.map((g) => (
                <div key={g.area} className="dynp-cgrp">
                  {/* the area name sits DIRECTLY in `.dynp-gh`, the same shape the
                      per-community headings on the other steps use, so one rule styles
                      every group heading in this picker */}
                  {/* the count says WHAT it counts — a bare number in a header that sits
                      above homes and plans counts reads as any of them */}
                  <div className="dynp-gh">{g.area}
                    <span className="dynp-ghc">{g.rows.length} {g.rows.length === 1 ? "community" : "communities"}</span>
                  </div>
                  {view === "list" && (
                    <div className="dynp-rhd">
                      <span className="dynp-rck" aria-hidden="true"></span>
                      <span className="dynp-rn">Community</span>
                      <span className="dynp-rc">City</span>
                      <span className="dynp-rm">Homes</span>
                    </div>
                  )}
                  <div className={view === "list" ? "dynp-rows" : "dynp-grid comm"}>
                    {g.rows.map((c) => {
                      const on = cur.indexOf(lc(c.name)) > -1;
                      const pk = packs.find((p) => lc(p.name) === lc(c.name) || (c.code && p.code === c.code));
                      const img = (pk && pk.C.community && pk.C.community.hero) || c.hero || "";
                      /* ── THE HOMES COLUMN IS A NUMBER (2026-08-22, Jack: "show the
                         actual number of homes") ────────────────────────────────────
                         It used to read "in your division" on every row — provenance in a
                         column headed Homes. Three sources, most specific first: this
                         community's own loaded pack, then the division feed's count, then
                         nothing. NOTHING IS BLANK, not a dash and not a word: a count that
                         has not arrived is not a count of zero, and "in your division" was
                         never an answer to "how many homes". */
                      const packN = pk ? (pk.C.homesites || []).filter(notSold).length : null;
                      /* `c.homes` already carries the count for a plain community AND the
                         roll-up for a masterplan (see commRows) — one source, so a parent
                         row and its collections cannot disagree (2026-08-22) */
                      const feedN = typeof c.homes === "number" ? c.homes : commHomes[lc(c.name)];
                      const homesN = packN != null ? packN : (typeof feedN === "number" ? feedN : null);
                      const meta = homesN == null ? "" : String(homesN);
                      if (view === "list") {
                        return (
                          <button key={lc(c.name)} type="button" className={"dynp-row" + (on ? " on" : "")} onClick={() => toggle(lc(c.name))}>
                            <span className="dynp-rck">✓</span>
                            <span className="dynp-rn">{c.name}</span>
                            <span className="dynp-rc">{c.city || ""}</span>
                            <span className="dynp-rm">{meta}</span>
                          </button>
                        );
                      }
                      return (
                        <button key={lc(c.name)} type="button" className={"dynp-card" + (on ? " on" : "")} onClick={() => toggle(lc(c.name))}>
                          <span className={"dynp-cimg sm" + (img ? "" : " ph")} style={img ? { backgroundImage: `url('${img}')` } : undefined}>
                            <span className="dynp-ck">✓</span>
                          </span>
                          <span className="dynp-cn">{c.name}</span>
                          {/* the city is the one fact a photo cannot carry, and it is
                              what tells two similarly-named collections apart */}
                          {!!c.city && <span className="dynp-cc">{c.city}</span>}
                          {/* on a CARD the number needs its noun — there is no column
                              header above it to say what it counts */}
                          <span className="dynp-cm">{meta ? meta + (meta === "1" ? " home" : " homes") : ""}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {q.trim().length >= 2 && (
                <button type="button" className="dynp-all" onClick={searchEverywhere} disabled={searching}>
                  {searching ? "Searching everywhere…" : `Search everywhere for “${q.trim()}” →`}
                </button>
              )}
            </>
          )}

          {/* the FLOORPLAN / HOMESITE steps: cards GROUPED BY COMMUNITY */}
          {!connecting && (kind === "plans" || kind === "homes" || kind === "heroes") && (
            <>
              {!!directErr && (
                <div className="dynp-msg">
                  {directErr}
                  {!!String(divisionCode || "").trim() && (
                    /* the division feed builds cold on first ask (measured 2.4s–60s
                       depending on the division), so "nothing came back" is sometimes
                       just "not yet" — a retry beats closing and reopening */
                    <> <button type="button" className="dynp-retry" onClick={() => setReload((n) => n + 1)}>Try again</button></>
                  )}
                </div>
              )}
              {!packs.length && !directErr && <div className="dynp-msg">Loading the live inventory…</div>}
              {!!packs.length && !groups.length && <div className="dynp-msg">Nothing matches.</div>}
              {tableMode && !!tgroups.length && (
                <div className={"dynp-t " + (kind === "homes" ? "g7" : "g6")}>
                  <div className="dynp-tr dynp-thdr">
                    <span />
                    {tableHead(kind).map((h) => (<span key={h}>{h}</span>))}
                  </div>
                  {tgroups.map((g) => (
                    <div key={g.code}>
                      <div className="dynp-gh">{g.name}</div>
                      {g.rows.map((r) => (
                        <button key={r.key} type="button" disabled={r.disabled}
                          className={"dynp-tr" + (r.on ? " on" : "") + (r.disabled ? " dis" : "")}
                          onClick={() => toggle(r.key)}>
                          <span className="dynp-tck">✓</span>
                          {r.cells.map((c, i) => (
                            <span key={i} className={"k-" + c.kind}>
                              {c.kind === "chip"
                                ? <span className={"dynp-chip" + (c.chip === "warn" ? " warn" : c.chip === "cs" ? " cs" : "")}>{c.text}</span>
                                : <span>{c.text}</span>}
                            </span>
                          ))}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {!tableMode && groups.map((g) => (
                <div key={g.code}>
                  {/* one hero per community means the heroes grid has no group headings
                      (monolith L10409) */}
                  {kind !== "heroes" && <div className="dynp-gh">{g.name}</div>}
                  {view === "list" && kind !== "heroes"
                    ? <div className="dynp-rows">
                        {g.cards.map((c) => <DynRow key={c.key} c={c} onPick={() => toggle(c.key)} />)}
                      </div>
                    : <div className="dynp-grid">
                        {g.cards.map((c) => <DynCard key={c.key} c={c} onPick={() => toggle(c.key)} />)}
                      </div>}
                </div>
              ))}
            </>
          )}
        </div>

        <div className="dynp-ft">
          <span className="dynp-count">
            <b>{cur.length}</b> selected{footerMax(kind, cap, wiz)}
          </span>
          {/* ── THE BLANK CARD IS STILL REACHABLE, AS ONE LINE (2026-08-22) ────────
              The division feed answers with AVAILABLE inventory, not everything, so a
              requester whose home is not in this list needs a way through. It lives
              here rather than as a second control in the panel — Jack has removed a
              duplicate door from that panel twice already. */}
          {secAdd && onHandType ? (
            <button type="button" className="dynp-hand" data-echhandtype
              onClick={() => { onHandType(); onClose(); }}>or type one by hand</button>
          ) : null}
          <span className="dynp-fbtns">
            {/* ── WHY THIS IS *NOT* THE SCOPE DROPDOWN (2026-08-22) ─────────────────
                Jack: "why do you still have this button ‹ Narrow by community". Fair
                question, and they are genuinely different: the dropdown FILTERS the list
                in front of you and keeps the block's feature level where it is, while
                this walks the community → floorplans → homesites chain, which is what
                loads a per-community pack for each pick. The FEATURE LEVEL control is
                different again — it changes WHAT the block features, not how you got
                there. Left in place deliberately; if it goes, the chain loses its only
                entry from a direct step. */}
            {wiz && steps.indexOf(kind as WizLevel) > 0 && !direct
                ? <button type="button" className="dynp-b" onClick={back}>‹ Back</button>
                : <button type="button" className="dynp-b" onClick={onClose}>Cancel</button>}
            {isLast
              ? <button type="button" className="dynp-b pri" onClick={done} disabled={connecting || finishing}>{finishing ? "Adding…" : "Done"}</button>
              : <button type="button" className="dynp-b pri" onClick={next} disabled={connecting || finishing}>{primaryLabel(steps, kind)}</button>}
          </span>
        </div>
      </div>
    </div>
  );
}
